import { useCallback, useMemo, useState } from 'react';
import { MultiValueProps } from 'react-select';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import {
  Chip,
  InformationBox,
  useGoogleAutocomplete,
  useGooglePlace,
} from '@app/core';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import { Select, SelectProps } from './Select';
import { useCountries } from '@app/api';
import { validatePhoneNumberLength } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';

interface OptionType {
  value: string;
  label: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  chip: {
    margin: theme.marginBase / 4,
    padding: 0,
    height: '100%',
    display: 'flex',
    overflow: 'hidden',
    width: 'fit-content',
    alignItems: 'stretch',

    '& p:first-letter': {
      textTransform: 'capitalize',
    },
  },
  text: {
    lineHeight: '150%',
    padding: [
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase / 2,
      theme.marginBase,
    ],
  },
  removeIcon: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.marginBase / 2,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  },
  icon: {
    fontSize: theme.iconSize.small,
  },
  error: {
    marginTop: theme.marginBase,
  },
}));

export type Place = google.maps.places.PlaceResult;

interface Props extends Omit<SelectProps, 'onValueChange'> {
  // onValueChange?: (val?: Place | Place[]) => any;
  onValueChange?: (val?: any) => any;
}

const MultiValue = (props: MultiValueProps<OptionType>) => {
  const tag = props.data.label;
  const classes = useStyles();
  return (
    <Chip
      color="green"
      className={classes.chip}
      {...props.innerProps}
      key={tag}
    >
      <p className={classes.text}>{tag}</p>
      <div className={classes.removeIcon} {...props.removeProps}>
        <i className={classnames('ri-close-line', classes.icon)} />
      </div>
    </Chip>
  );
};
const components = {
  MultiValue,
};

const fields = [
  'geometry.location',
  'name',
  'formatted_address',
  'place_id',
  'address_components',
  'adr_address',
  'plus_code',
  'types',
];

export const PlaceSelect = ({ onValueChange, ...props }: Props) => {
  const [inputValue, setInputValue] = useState('');
  const classes = useStyles();
  const { t } = useTranslation('common');

  const [blacklistCountryError, setBlacklistCountryError] = useState(false);

  const completionService = useGoogleAutocomplete();
  const placeService = useGooglePlace();
  const { data: countries = [] } = useCountries();

  const [knownPlaceDetails, setKnownPlaceDetails] = useState<
    Record<string, Place> | undefined
  >({});

  const isMulti = props.isMulti;
  const checkBlacklistedValueChange = useCallback(
    (values: Place | Place[]) => {
      if (!onValueChange) {
        return;
      }
      const places = Array.isArray(values) ? values : [values];
      const res = places.filter((p) => {
        return countries.some((c) =>
          p.address_components?.some(
            (comp) =>
              comp.types.includes('country') && comp.short_name === c.key,
          ),
        );
      });

      if (res.length !== places.length) {
        setBlacklistCountryError(true);
      } else {
        setBlacklistCountryError(false);
      }
      onValueChange(res);
    },
    [onValueChange, countries, isMulti],
  );

  const _onChange = useCallback(
    async (value: string | string[]) => {
      if (!onValueChange) {
        return;
      }
      if (Array.isArray(value)) {
        const valuesPromises = value.map((v: string) => {
          if (knownPlaceDetails && knownPlaceDetails[v]) {
            return knownPlaceDetails[v];
          }
          return new Promise((resolve) =>
            placeService?.getDetails(
              {
                placeId: v,
                fields,
              },
              resolve,
            ),
          );
        });
        const values = (await Promise.all(valuesPromises)) as Place[];

        setKnownPlaceDetails((currPlaceDetails) =>
          values.reduce(
            (acc, val) => {
              if (!val.place_id) {
                return acc;
              }
              acc[val.place_id] = val;
              return acc;
            },
            { ...currPlaceDetails } as Record<string, Place>,
          ),
        );

        checkBlacklistedValueChange(values as any);
        return;
      }

      const res = await new Promise((resolve) =>
        placeService?.getDetails(
          {
            placeId: value,
            fields,
          },
          resolve,
        ),
      );
      setKnownPlaceDetails((prev) => ({
        ...prev,
        [value]: res as Place,
      }));
      checkBlacklistedValueChange(res as any);
    },
    [
      checkBlacklistedValueChange,
      placeService,
      knownPlaceDetails,
      setKnownPlaceDetails,
      validatePhoneNumberLength,
    ],
  );

  const [results, setResults] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);

  const autocomplete = useCallback(
    debounce(
      async (text: string) => {
        if (!completionService) {
          return;
        }
        const res = await completionService.getPlacePredictions({
          input: text,
          types: ['(cities)'],
        });
        setResults(res.predictions);
      },
      300,
      {
        leading: true,
      },
    ),
    [completionService, setResults],
  );

  const options = useMemo(() => {
    return results.map((result) => {
      return { value: result.place_id, label: result.description };
    });
  }, [results]);

  const onTypeText = useCallback(
    (inputValue: string) => {
      setInputValue(inputValue);
      if (inputValue.length === 0) {
        return;
      }
      autocomplete(inputValue);
    },
    [autocomplete],
  );

  return (
    <>
      <Select
        {...props}
        options={options}
        small
        components={components as any}
        onValueChange={_onChange}
        inputValue={inputValue}
        onInputChange={onTypeText}
        withAutocomplete
      />
      {blacklistCountryError && (
        <InformationBox
          variant="danger"
          className={classes.error}
          text={t('countryError')}
        />
      )}
    </>
  );
};
